import type { Template } from '@pdfme/common'

const FONT_NAME = 'Sarabun'

/**
 * Replace all {{variable}} placeholders in a string.
 * Supports dot-notation keys like {{property.name}} and flat keys like {{prop_name}}.
 */
export function replaceVariables(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (_match, key) => vars[key] ?? '')
}

/**
 * Build pdfme inputs by scanning template schemas for `content` fields with {{placeholders}}.
 * This is the proven approach used by the working lease PDF generator.
 *
 * - Text schemas with `content` are processed (placeholders replaced)
 * - Barcode schemas with `content` are also processed
 * - Line schemas and schemas without `content` are skipped
 */
export function buildInputsFromTemplate(
  template: Template,
  vars: Record<string, string>
): Record<string, string>[] {
  const merged: Record<string, string> = {}

  for (const page of template.schemas) {
    for (const field of page) {
      const schema = field as Record<string, unknown>
      const name = schema.name as string
      const type = schema.type as string

      if (typeof schema.content === 'string') {
        if (type === 'text' || type === 'qrcode' || type?.startsWith('barcodes.')) {
          merged[name] = replaceVariables(schema.content as string, vars)
        }
      }
    }
  }

  return [merged]
}

/**
 * Ensure every text schema has fontName set to the Thai font.
 * pdfme v5 requires explicit fontName on text schemas to use custom fonts;
 * without it, the built-in default font is used which cannot render Thai characters.
 */
export function ensureFontName(template: Template): Template {
  return {
    ...template,
    schemas: template.schemas.map((page) =>
      page.map((schema) => {
        if ((schema as Record<string, unknown>).type === 'text' && !(schema as Record<string, unknown>).fontName) {
          return { ...schema, fontName: FONT_NAME } as typeof schema
        }
        return schema
      })
    ),
  }
}
